import {
  canReadVerifyReport,
  canRunVerify,
} from "@/features/verify/verify-rights";

type TabsRightsUser = {
  rights: ReadonlySet<string>;
  admin?: unknown;
};

// One right for the whole tab, read and write alike. Front gate only: the
// agent Lambda accepts any logged-in account, and each LCM tool then checks
// its own tab right.
const AGENT_RIGHT = "lcm_tabs.ai_agent";

function canOpenAgent(user: TabsRightsUser): boolean {
  return user.rights.has(AGENT_RIGHT);
}

export type CampaignTab = keyof ReturnType<typeof buildTabsRights>;

export function buildTabsRights(user: TabsRightsUser): {
  "insertion-order": boolean;
  "asset-manager": boolean;
  "campaign-setup": boolean;
  dco: boolean;
  history: boolean;
  footfall: boolean;
  "geo-planner": boolean;
  "format-builder": boolean;
  "tracking-manager": boolean;
  monitoring: boolean;
  verify: boolean;
  "pacer-budgets": boolean;
  agent: boolean;
} {
  // Reading a tab needs read OR write, as in v1 and as the API itself allows:
  // requiring both locked read-only accounts out of the whole campaign.
  const canOpen = (scope: string): boolean =>
    user.rights.has(`lcm_tabs.${scope}_read`) ||
    user.rights.has(`lcm_tabs.${scope}_write`);

  return {
    "insertion-order": canOpen("insertion_order"),
    "asset-manager": canOpen("asset_manager"),
    "campaign-setup": canOpen("campaign_overview"),
    dco: canOpen("asset_manager"),
    history: canOpen("history"),
    footfall: canOpen("footfall_settings"),
    "geo-planner": canOpen("geo_planner"),
    "format-builder": canOpen("format_builder"),
    "tracking-manager": canOpen("tracking_manager"),
    monitoring: canOpen("monitoring"),
    // The report spans every module tab, so reading any one of them is
    // enough to see the summary.
    verify: canReadVerifyReport(user.rights),
    // Live budget and buying-cost figures: Mobsuccess staff only, mirroring the
    // server-side admin check on the platform endpoint this tab reads.
    "pacer-budgets": Boolean(user.admin),
    agent: canOpenAgent(user),
  };
}

// Editing needs the write right alone: the API refuses a PATCH/POST/PUT/DELETE
// from an account that only holds the read one, so the actions are hidden
// rather than left to fail on save.
export function buildTabsWriteRights(
  user: TabsRightsUser,
): Record<CampaignTab, boolean> {
  const canWrite = (scope: string): boolean =>
    user.rights.has(`lcm_tabs.${scope}_write`);

  return {
    "insertion-order": canWrite("insertion_order"),
    "asset-manager": canWrite("asset_manager"),
    "campaign-setup": canWrite("campaign_overview"),
    dco: canWrite("asset_manager"),
    history: canWrite("history"),
    footfall: canWrite("footfall_settings"),
    "geo-planner": canWrite("geo_planner"),
    "format-builder": canWrite("format_builder"),
    "tracking-manager": canWrite("tracking_manager"),
    monitoring: canWrite("monitoring"),
    // Running a verification writes a report, so it needs a module write
    // right; the pacer tab stays gated on the admin flag.
    verify: canRunVerify(user.rights),
    "pacer-budgets": Boolean(user.admin),
    // Same single right as reading: there is no `_read`/`_write` split.
    agent: canOpenAgent(user),
  };
}

// Campaign-level actions (validate, send brief) make no sense for an account
// that cannot write in any module tab; verify, the admin-gated pacer tab and
// the read-only agent do not count as write surfaces.
const NON_WRITE_SURFACES: ReadonlySet<string> = new Set([
  "verify",
  "pacer-budgets",
  "agent",
]);

export function hasAnyModuleWriteRight(
  writeRights: Record<CampaignTab, boolean>,
): boolean {
  return Object.entries(writeRights).some(
    ([tab, granted]) => granted && !NON_WRITE_SURFACES.has(tab),
  );
}
